import { getQuotaDependentFieldLabel } from './common';
import { formatNumber } from '@/components/global/mixins';
import { jobsAeQuotaPeriodNames } from '@/components/partials/jobs/constants/optionsDicts';
import {
  QUOTA_ATTAINMENT_BASE_LABEL,
  QUOTA_ATTAINMENT_LAST_PERIOD__SHORT_LABEL,
} from '@/components/partials/jobs/JobsProfile/constants';
import type { InquiryForm } from '@/components/partials/public-portfolio/PortfolioJobsInquirySection/PortfolioJobsInquirySectionProfile/types';
import type { JobsInquiry, JobsModeratorsInquiry } from '@/types/graphql';
import { JobsInquiryAeQuota } from '@/types/graphql';
import { getCurrencyData } from '~/components/partials/jobs/utils';

export const getAeAnnualizedQuota = (inquiry: JobsInquiry | JobsModeratorsInquiry) => {
  if (!inquiry.aeQuotaTypeMain || !inquiry.aeQuotaPerPeriod) return null;

  const multiplicator = {
    [JobsInquiryAeQuota.Monthly]: 12,
    [JobsInquiryAeQuota.Quarterly]: 4,
    [JobsInquiryAeQuota.Annual]: 1,
  };

  return multiplicator[inquiry.aeQuotaTypeMain] * inquiry.aeQuotaPerPeriod;
};

export const getAeQuotaFieldName = (inquiry: InquiryForm) => {
  const baseLabel = 'Quota';

  if (inquiry?.aeQuotaTypeOther) return baseLabel;
  if (!inquiry?.aeQuotaTypeMain) return baseLabel;

  return getQuotaDependentFieldLabel({ periodKey: inquiry.aeQuotaTypeMain, baseLabel, periodPrefix: 'per' });
};

export const getAeQuotaAttainmentFieldName = (inquiry: InquiryForm) => {
  const baseLabel = 'Quota attainment';

  if (inquiry?.aeQuotaTypeOther) return baseLabel;
  if (!inquiry?.aeQuotaTypeMain) return baseLabel;

  return getQuotaDependentFieldLabel({ periodKey: inquiry.aeQuotaTypeMain, baseLabel, periodPrefix: 'last' });
};

export const getAeQuotaAttainmentEditFieldLabel = (inquiry: InquiryForm) => {
  if (inquiry?.aeQuotaTypeOther) return QUOTA_ATTAINMENT_LAST_PERIOD__SHORT_LABEL;
  if (!inquiry?.aeQuotaTypeMain) return QUOTA_ATTAINMENT_LAST_PERIOD__SHORT_LABEL;

  return getQuotaDependentFieldLabel({
    periodKey: inquiry.aeQuotaTypeMain,
    baseLabel: QUOTA_ATTAINMENT_BASE_LABEL,
    periodPrefix: 'last',
    postfix: '?',
  });
};

export const getAeQuotaFieldValue = (inquiry: InquiryForm) => {
  if (!inquiry.aeQuotaPerPeriod) return undefined;

  const { symbol } = getCurrencyData(inquiry.currency);
  return `${symbol}${formatNumber.methods.numberToDelimitedFormatter(String(inquiry.aeQuotaPerPeriod))}`;
};

export const getIsAeQuotaAttainmentHidden = (inquiry: InquiryForm) => {
  return inquiry?.aeQuotaTypeMain === JobsInquiryAeQuota.Annual;
};

export const getAeLastYearQuotaAttainment = (inquiry: InquiryForm) => {
  if (!inquiry?.quotaAttainmentPerLastYear) return undefined;

  return `${inquiry.quotaAttainmentPerLastYear}%`;
};

export const getAeQuotaTypeFieldValue = (inquiry: InquiryForm) => {
  if (inquiry?.aeQuotaTypeMain) return jobsAeQuotaPeriodNames[inquiry.aeQuotaTypeMain];

  return inquiry?.aeQuotaTypeOther || undefined;
};
